(() => {
  const ELEMENT_NAME = 'cuero-card-loop-gallery-v16';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (from, to, amount) => from + (to - from) * amount;

  class CueroCardLoopGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.gallery = this.querySelector('[data-clcg-gallery]');
      this.cardsList = this.querySelector('[data-clcg-cards]');
      this.cards = Array.from(this.cardsList?.querySelectorAll('[data-clcg-card]') || []);
      this.originalCards = this.cards.slice();
      this.originalCards.forEach((card, index) => {
        card.dataset.clcgCardIndex = String(index);
      });
      this.prevButton = this.querySelector('[data-clcg-prev]');
      this.nextButton = this.querySelector('[data-clcg-next]');

      if (!this.gallery || this.cards.length === 0) return;

      this.initialized = true;
      this.dataset.clcgVersion = '20260906-touch-render-fix-v23';
      this.current = 0;
      this.target = 0;
      this.cardStep = 240;
      this.lastFrameTime = 0;
      this.pointerId = null;
      this.dragInput = null;
      this.dragging = false;
      this.suppressClick = false;
      this.lastDeltaX = 0;
      this.lastDeltaY = 0;
      this.lastDragX = 0;
      this.lastDragTime = 0;
      this.dragVelocityX = 0;
      this.snapTimer = null;
      this.autoplayTimer = null;
      this.frame = null;

      this.handleScroll = this.onScroll.bind(this);
      this.handleResize = this.onResize.bind(this);
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handleTouchStart = this.onTouchStart.bind(this);
      this.handleTouchMove = this.onTouchMove.bind(this);
      this.handleTouchEnd = this.onTouchEnd.bind(this);
      this.handleWheel = this.onWheel.bind(this);
      this.handleClickCapture = this.onClickCapture.bind(this);
      this.handleAnchorClick = this.onAnchorClick.bind(this);
      this.handleObservedResize = this.onObservedResize.bind(this);
      this.handleSettingsMutation = this.onSettingsMutation.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);
      this.handlePrev = () => this.goBy(-1);
      this.handleNext = () => this.goBy(1);
      this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(this.handleObservedResize) : null;
      this.mutationObserver = 'MutationObserver' in window ? new MutationObserver(this.handleSettingsMutation) : null;

      this.readSettings();
      this.ensureLoopDensity();
      this.prepareImages();
      this.measure(true);
      this.bind();
      this.onScroll();
      this.render(true);
      this.tick();
      this.startAutoplay();
      this.handleInitialHash();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      this.gallery?.removeEventListener('pointerdown', this.handlePointerDown);
      this.gallery?.removeEventListener('pointermove', this.handlePointerMove);
      this.gallery?.removeEventListener('pointerup', this.handlePointerUp);
      this.gallery?.removeEventListener('pointercancel', this.handlePointerUp);
      this.gallery?.removeEventListener('touchstart', this.handleTouchStart);
      this.gallery?.removeEventListener('touchmove', this.handleTouchMove);
      this.gallery?.removeEventListener('touchend', this.handleTouchEnd);
      this.gallery?.removeEventListener('touchcancel', this.handleTouchEnd);
      document.removeEventListener('touchmove', this.handleTouchMove);
      document.removeEventListener('touchend', this.handleTouchEnd);
      document.removeEventListener('touchcancel', this.handleTouchEnd);
      document.removeEventListener('click', this.handleAnchorClick, true);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.gallery?.removeEventListener('wheel', this.handleWheel);
      this.removeEventListener('click', this.handleClickCapture, true);
      this.prevButton?.removeEventListener('click', this.handlePrev);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.imageObserver?.disconnect();
      clearTimeout(this.snapTimer);
      clearInterval(this.autoplayTimer);
      cancelAnimationFrame(this.frame);
    }

    bind() {
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.gallery.addEventListener('pointerdown', this.handlePointerDown);
      this.gallery.addEventListener('pointermove', this.handlePointerMove, { passive: false });
      this.gallery.addEventListener('pointerup', this.handlePointerUp);
      this.gallery.addEventListener('pointercancel', this.handlePointerUp);
      this.gallery.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      this.gallery.addEventListener('wheel', this.handleWheel, { passive: false });
      this.addEventListener('click', this.handleClickCapture, true);
      document.addEventListener('click', this.handleAnchorClick, true);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.prevButton?.addEventListener('click', this.handlePrev);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.resizeObserver?.observe(this.gallery);
      this.cards
        .filter((card) => !card.hasAttribute('data-clcg-clone'))
        .forEach((card) => this.resizeObserver?.observe(card));
      this.mutationObserver?.observe(this, {
        attributes: true,
        attributeFilter: [
          'data-layout',
          'data-mobile-stack-order',
          'data-show-card-text',
          'data-show-header',
          'data-behind-overlay',
          'data-card-shadow',
          'data-card-count',
          'data-scroll-enabled',
          'data-scroll-items',
          'data-snap',
          'data-drag-enabled',
          'data-wheel-enabled',
          'data-drag-sensitivity',
          'data-wheel-sensitivity',
          'data-visible-range',
          'data-card-spread',
          'data-mobile-card-spread',
          'data-side-scale',
          'data-side-opacity',
          'data-autoplay',
          'data-autoplay-delay'
        ]
      });
    }

    readSettings() {
      this.applyStateClasses();
      this.scrollEnabled = this.dataset.scrollEnabled === 'true';
      this.snapEnabled = this.dataset.snap === 'true';
      this.dragEnabled = this.dataset.dragEnabled === 'true';
      this.wheelEnabled = this.dataset.wheelEnabled === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.scrollItems = Math.max(
        this.readNumber('scrollItems', Math.max(this.originalCards.length - 1, 1)),
        Math.max(this.originalCards.length - 1, 1)
      );
      this.dragSensitivity = this.readNumber('dragSensitivity', 100) / 100;
      this.wheelSensitivity = this.readNumber('wheelSensitivity', 45) / 100;
      this.visibleRange = clamp(this.readNumber('visibleRange', 2), 1, 5);
      this.desktopSpread = this.readNumber('cardSpread', 115) / 100;
      this.mobileSpread = this.readNumber('mobileCardSpread', 82) / 100;
      this.minScale = clamp(this.readNumber('sideScale', 58) / 100, 0.1, 1);
      this.minOpacity = clamp(this.readNumber('sideOpacity', 42) / 100, 0, 1);
      this.autoplayDelay = this.readNumber('autoplayDelay', 3500);
    }

    applyStateClasses() {
      const layout = this.dataset.layout === 'image_only' ? 'image_only' : 'split';
      const mobileOrder = this.dataset.mobileStackOrder === 'text_first' ? 'text_first' : 'image_first';

      this.classList.toggle('clcg--layout-split', layout === 'split');
      this.classList.toggle('clcg--layout-image_only', layout === 'image_only');
      this.classList.toggle('clcg--mobile-image_first', mobileOrder === 'image_first');
      this.classList.toggle('clcg--mobile-text_first', mobileOrder === 'text_first');
      this.classList.toggle('clcg--show-card-text', this.dataset.showCardText === 'true');
      this.classList.toggle('clcg--show-header', this.dataset.showHeader === 'true');
      this.classList.toggle('clcg--behind-overlay', this.dataset.behindOverlay === 'true');
      this.classList.toggle('clcg--card-shadow', this.dataset.cardShadow === 'true');
    }

    ensureLoopDensity() {
      const originals = this.originalCards;
      if (!this.cardsList || originals.length === 0) return;

      const requiredCount = Math.max(7, this.visibleRange * 2 + 3);
      let cloneIndex = 0;

      while (this.cards.length < requiredCount) {
        const clone = originals[cloneIndex % originals.length].cloneNode(true);
        clone.setAttribute('data-clcg-clone', 'true');
        clone.removeAttribute('data-active');
        clone.removeAttribute('id');

        Array.from(clone.attributes).forEach((attribute) => {
          if (attribute.name.indexOf('data-shopify') === 0) clone.removeAttribute(attribute.name);
        });

        clone.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });

        this.cardsList.appendChild(clone);
        this.cards.push(clone);
        cloneIndex += 1;
      }
    }

    prepareImages() {
      const images = this.originalCards
        .map((card) => card.querySelector('.clcg__card-media-image'))
        .filter(Boolean);

      if (images.length === 0) return;

      const startLoading = () => {
        images.forEach((image) => {
          image.loading = 'eager';
          image.fetchPriority = 'low';
        });
      };

      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          startLoading();
          this.imageObserver?.disconnect();
        }, { rootMargin: '1200px 0px' });
        this.imageObserver.observe(this);
      } else {
        startLoading();
      }
    }

    readNumber(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    measure(recalculateMobileCardHeight = false) {
      this.updateMobileRequiredHeight(recalculateMobileCardHeight);
      const firstCard = this.cards[0];
      const rect = firstCard.getBoundingClientRect();
      const computedWidth = parseFloat(window.getComputedStyle(firstCard).width);
      const galleryRect = this.gallery.getBoundingClientRect();
      // getBoundingClientRect() includes the card's initial scale(0.01), so it
      // can report a 224px card as roughly 2px wide. Use layout width first.
      this.cardWidth = firstCard.offsetWidth || computedWidth || rect.width || 224;
      const configuredStep = this.cardWidth * (mobileQuery.matches ? this.mobileSpread : this.desktopSpread);
      const cardCenter = firstCard.offsetLeft || galleryRect.width / 2;
      const sideRoom = Math.max(1, Math.min(cardCenter, galleryRect.width - cardCenter));
      const minimumStep = this.cardWidth * (mobileQuery.matches ? 0.54 : 0.62);
      const maximumStep = Math.max(minimumStep, Math.min(this.cardWidth * 1.24, sideRoom * 0.62));
      this.cardStep = clamp(configuredStep, minimumStep, maximumStep);
    }

    updateMobileRequiredHeight(recalculateCardHeight = false) {
      const isMobileSplit = mobileQuery.matches && this.classList.contains('clcg--layout-split');

      if (!isMobileSplit) {
        if (this.style.getPropertyValue('--clcg-mobile-required-height')) {
          this.style.removeProperty('--clcg-mobile-required-height');
        }
        if (this.style.getPropertyValue('--clcg-mobile-card-height')) {
          this.style.removeProperty('--clcg-mobile-card-height');
        }
        this.mobileCardHeight = 0;
        return;
      }

      const originalCards = this.cards.filter((card) => !card.hasAttribute('data-clcg-clone'));
      const styles = window.getComputedStyle(this);
      const configuredMinimum = parseFloat(styles.getPropertyValue('--clcg-split-mobile-min-height')) || 470;
      let tallestCard = this.mobileCardHeight || parseFloat(this.style.getPropertyValue('--clcg-mobile-card-height')) || 0;

      if (recalculateCardHeight || tallestCard <= 0) {
        this.style.removeProperty('--clcg-mobile-card-height');
        void this.offsetHeight;

        tallestCard = originalCards.reduce((maximum, card) => {
          const link = card.querySelector('.clcg__card-link');
          const height = Math.max(
            configuredMinimum,
            card.offsetHeight,
            link?.offsetHeight || 0,
            link?.scrollHeight || 0
          );
          return Math.max(maximum, Math.ceil(height));
        }, configuredMinimum);

        this.mobileCardHeight = tallestCard;
        this.style.setProperty('--clcg-mobile-card-height', `${tallestCard}px`);
      }

      const configuredAnchor = parseFloat(styles.getPropertyValue('--clcg-cards-top-mobile'));
      const anchor = clamp((Number.isFinite(configuredAnchor) ? configuredAnchor : 43) / 100, 0.15, 0.85);
      const controlsHeight = this.querySelector('.clcg__actions')?.offsetHeight || 0;
      const configuredActionsBottom = parseFloat(styles.getPropertyValue('--clcg-actions-bottom'));
      const actionsBottom = Number.isFinite(configuredActionsBottom) ? configuredActionsBottom : 25;
      const topRequiredHeight = (tallestCard / 2 + 14) / anchor;
      const bottomReserve = controlsHeight > 0 ? controlsHeight + actionsBottom + 18 : 24;
      const bottomRequiredHeight = (tallestCard / 2 + bottomReserve) / (1 - anchor);
      const requiredHeight = Math.ceil(Math.max(560, topRequiredHeight, bottomRequiredHeight));
      const nextValue = `${requiredHeight}px`;

      if (this.style.getPropertyValue('--clcg-mobile-required-height').trim() !== nextValue) {
        this.style.setProperty('--clcg-mobile-required-height', nextValue);
      }
    }

    tick(timestamp = performance.now()) {
      const elapsed = this.lastFrameTime > 0 ? (timestamp - this.lastFrameTime) / 1000 : 1 / 60;
      const deltaTime = clamp(elapsed, 1 / 240, 0.05);
      this.lastFrameTime = timestamp;

      if (reducedMotion.matches) {
        this.current = this.target;
      } else {
        const distance = this.target - this.current;
        // Live dragging uses a short, frame-rate-independent smoothing window:
        // responsive enough to feel attached, but filtered enough to remove
        // the small jumps produced by raw mobile touch coordinates.
        const response = this.dragging && !mobileQuery.matches ? 32 : (mobileQuery.matches ? 8.25 : 10.5);
        const amount = 1 - Math.exp(-response * deltaTime);
        this.current = Math.abs(distance) < 0.0015 ? this.target : lerp(this.current, this.target, amount);
      }

      this.render(false);
      this.frame = requestAnimationFrame(() => this.tick());
    }

    render(force) {
      if (force) this.current = this.target;

      const renderCards = this.originalCards.length > 0 ? this.originalCards : this.cards;
      const count = renderCards.length;
      if (!count) return;

      this.cards.forEach((card) => {
        card.style.opacity = '0';
        card.style.pointerEvents = 'none';
        card.style.zIndex = '0';
        card.setAttribute('aria-hidden', 'true');
        card.toggleAttribute('data-active', false);
        card.querySelector('.clcg__card-link[href]')?.setAttribute('tabindex', '-1');
      });

      const wrappedCurrent = this.mod(this.current, count);
      const activeIndex = this.mod(Math.round(this.current), count);

      renderCards.forEach((card, cardIndex) => {
        let position = cardIndex - wrappedCurrent;

        if (position > count / 2) position -= count;
        if (position < count / -2) position += count;

        const distance = Math.abs(position);
        if (distance > this.visibleRange + 0.98) return;

        const progress = clamp(distance / Math.max(this.visibleRange, 1), 0, 1);
        const scale = 1 - progress * (1 - this.minScale);
        const opacity = 1 - progress * (1 - this.minOpacity);
        const x = position * this.cardStep;
        const y = Math.pow(progress, 1.8) * 4;
        const zIndex = Math.round((this.visibleRange + 1 - distance) * 100);

        card.style.transform = `translate(-50%, -50%) translate3d(${x}px, ${y}px, 0) scale(${scale})`;
        card.style.opacity = opacity.toFixed(3);
        card.style.zIndex = String(zIndex);
        card.style.pointerEvents = opacity > 0.25 ? 'auto' : 'none';
        const isActive = cardIndex === activeIndex;
        card.toggleAttribute('data-active', isActive);
        card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) {
          card.querySelector('.clcg__card-link[href]')?.setAttribute('tabindex', '0');
        }
      });
    }

    mod(value, count) {
      return ((value % count) + count) % count;
    }

    safeDecode(value) {
      try {
        return decodeURIComponent(value);
      } catch (error) {
        return value;
      }
    }

    cleanCardId(value) {
      const raw = String(value || '').trim();
      const withoutHash = raw.charAt(0) === '#' ? raw.slice(1) : raw;
      return this.safeDecode(withoutHash).trim();
    }

    toHandle(value) {
      return String(value || '')
        .trim()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    getOriginalCardIndex(card) {
      if (!card || this.originalCards.length === 0) return -1;

      const cardIndex = Number(card.dataset?.clcgCardIndex);
      if (Number.isInteger(cardIndex) && cardIndex >= 0 && cardIndex < this.originalCards.length) {
        return cardIndex;
      }

      return this.originalCards.indexOf(card);
    }

    resolveCardIndex(target) {
      const count = this.originalCards.length;
      if (!count) return -1;

      if (typeof target === 'number') {
        return Number.isInteger(target) && target >= 0 && target < count ? target : -1;
      }

      if (typeof target === 'string') {
        const cleanId = this.cleanCardId(target);
        if (!cleanId) return -1;

        const handle = this.toHandle(cleanId);
        const exactMatch = this.originalCards.find((card, index) => {
          const candidates = [
            card.id,
            card.dataset.clcgCardId,
            card.dataset.clcgAnchor,
            card.dataset.clcgCardIndex,
            String(index),
            String(index + 1)
          ].filter(Boolean);

          return candidates.some((candidate) => candidate === cleanId || this.toHandle(candidate) === handle);
        });

        if (exactMatch) return this.getOriginalCardIndex(exactMatch);

        const titleMatch = this.originalCards.find((card) => {
          const title = card.querySelector('.clcg__card-title')?.textContent || '';
          return title.trim() === cleanId || this.toHandle(title) === handle;
        });

        return titleMatch ? this.getOriginalCardIndex(titleMatch) : -1;
      }

      const cardElement = target?.closest?.('[data-clcg-card]') || target;
      return this.getOriginalCardIndex(cardElement);
    }

    findNearestTargetForCard(cardIndex, keepInsideScrollRange) {
      const loopLength = this.originalCards.length || this.cards.length;
      if (!loopLength) return cardIndex;

      const normalizedIndex = this.mod(cardIndex, loopLength);
      const reference = Number.isFinite(this.target) ? this.target : 0;
      const nearestCycle = Math.round((reference - normalizedIndex) / loopLength);
      const candidates = [];

      for (let cycleOffset = -2; cycleOffset <= 2; cycleOffset += 1) {
        const candidate = normalizedIndex + (nearestCycle + cycleOffset) * loopLength;
        if (keepInsideScrollRange && (candidate < 0 || candidate > this.scrollItems)) continue;
        candidates.push(candidate);
      }

      if (candidates.length === 0) {
        return keepInsideScrollRange ? clamp(normalizedIndex, 0, this.scrollItems) : normalizedIndex;
      }

      return candidates.reduce((nearest, candidate) => {
        return Math.abs(candidate - reference) < Math.abs(nearest - reference) ? candidate : nearest;
      }, candidates[0]);
    }

    showCard(card, options = {}) {
      const cardIndex = this.resolveCardIndex(card);
      if (cardIndex < 0) return false;

      this.restartAutoplay();

      if (this.scrollEnabled) {
        const nextTarget = this.findNearestTargetForCard(cardIndex, true);
        this.scrollToTarget(nextTarget);
      } else {
        this.target = this.findNearestTargetForCard(cardIndex, false);
        if (this.snapEnabled) this.snapTarget();

        if (reducedMotion.matches) {
          this.current = this.target;
          this.render(true);
        }

        const offset = Math.max(0, Number(options.offset) || this.getAnchorOffset());
        window.scrollTo({
          top: Math.max(0, this.getSectionTop() - offset),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }

      return true;
    }

    showCardById(cardId, options = {}) {
      return this.showCard(cardId, options);
    }

    getSamePageHash(link) {
      const href = link?.getAttribute?.('href');
      if (!href || href === '#') return '';

      try {
        const url = new URL(href, window.location.href);
        if (url.origin !== window.location.origin) return '';
        if (url.pathname !== window.location.pathname) return '';
        if (url.search !== window.location.search) return '';
        return url.hash ? this.cleanCardId(url.hash) : '';
      } catch (error) {
        return href.charAt(0) === '#' ? this.cleanCardId(href) : '';
      }
    }

    onAnchorClick(event) {
      if (this.suppressClick || event.defaultPrevented) return;

      const link = event.target?.closest?.('a[href]');
      const cardId = this.getSamePageHash(link);
      if (!cardId) return;

      if (this.showCard(cardId, { offset: this.getAnchorOffset() })) {
        event.preventDefault();
        window.history?.pushState?.(null, '', `#${cardId}`);
      }
    }

    handleInitialHash() {
      if (!window.location.hash) return;

      window.setTimeout(() => {
        this.showCard(window.location.hash, { offset: this.getAnchorOffset() });
      }, 0);
    }

    onScroll() {
      if (!this.scrollEnabled || this.dragging) return;

      const top = this.getSectionTop();
      const distance = this.getScrollDistance();
      const progress = clamp((window.scrollY - top) / distance, 0, 1);
      this.target = progress * this.scrollItems;

      if (this.snapEnabled) this.scheduleScrollSnap();
    }

    onResize() {
      this.measure(true);
      this.onScroll();
      this.render(true);
    }

    onObservedResize() {
      this.measure(false);
      this.onScroll();
      this.render(true);
    }

    onSettingsMutation() {
      this.readSettings();
      this.ensureLoopDensity();
      this.measure(true);
      this.onScroll();
      this.render(true);
      this.restartAutoplay();
    }

    onBlockSelect(event) {
      const card = event.target?.closest?.('[data-clcg-card]');
      if (!card || !this.contains(card) || card.hasAttribute('data-clcg-clone')) return;
      this.showCard(card, { offset: this.getAnchorOffset() });
    }

    onWheel(event) {
      if (this.scrollEnabled || !this.wheelEnabled || this.cards.length <= 1) return;
      if (event.target.closest('[data-clcg-prev], [data-clcg-next]')) return;

      event.preventDefault();
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      this.target += delta * this.wheelSensitivity * 0.01;
      this.restartAutoplay();
      if (this.snapEnabled) this.scheduleTargetSnap();
    }

    canStartDrag(target) {
      if (!this.dragEnabled || this.cards.length <= 1) return false;
      return !target.closest('[data-clcg-prev], [data-clcg-next]');
    }

    beginDrag(identifier, x, y, input) {
      if (this.pointerId !== null) return false;

      this.pointerId = identifier;
      this.dragInput = input;
      this.startX = x;
      this.startY = y;
      this.startTarget = this.target;
      this.lastDeltaX = 0;
      this.lastDeltaY = 0;
      this.lastDragX = x;
      this.lastDragTime = performance.now();
      this.dragVelocityX = 0;
      this.dragging = false;
      clearTimeout(this.snapTimer);
      this.restartAutoplay();
      return true;
    }

    updateDrag(x, y, event) {
      const deltaX = x - this.startX;
      const deltaY = y - this.startY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      const now = performance.now();
      const sampleDuration = clamp(now - this.lastDragTime, 1, 80);
      const instantVelocity = clamp((x - this.lastDragX) / sampleDuration, -3.5, 3.5);

      // A filtered release velocity prevents one noisy touch sample from
      // turning a gentle swipe into a harsh multi-card jump.
      this.dragVelocityX = lerp(this.dragVelocityX, instantVelocity, 0.38);
      this.lastDragX = x;
      this.lastDragTime = now;

      this.lastDeltaX = deltaX;
      this.lastDeltaY = deltaY;

      if (!this.dragging && absY > 9 && absY > absX * 1.15) {
        this.endDrag(false);
        return;
      }

      if (!this.dragging && absX > 7 && absX > absY * 1.02) {
        this.dragging = true;
        this.classList.add('is-dragging');
      }

      if (!this.dragging) return;

      if (event?.cancelable) event.preventDefault();
      const rawTarget = this.startTarget - (deltaX / this.cardStep) * this.dragSensitivity;

      if (mobileQuery.matches && this.snapEnabled) {
        // Follow the finger continuously, but limit a single mobile gesture to
        // one neighbouring card. Both live movement and release use the same
        // frame-rate-independent easing as button navigation.
        const oneCardTravel = clamp(rawTarget - this.startTarget, -1, 1);
        this.setDragTarget(this.startTarget + oneCardTravel);
      } else {
        this.setDragTarget(rawTarget);
      }
    }

    endDrag(shouldSnap = true) {
      const wasDragging = this.dragging;
      const releasedPointerId = this.pointerId;
      const releasedInput = this.dragInput;

      if (wasDragging) {
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 320);

        if (shouldSnap && this.snapEnabled) {
          this.snapAfterDrag();
        } else if (!shouldSnap) {
          this.target = this.startTarget;
        }
      }

      if (releasedInput === 'pointer' && releasedPointerId !== null) {
        try {
          this.gallery.releasePointerCapture?.(releasedPointerId);
        } catch (error) {
          // The browser may already have released it after pointercancel.
        }
      }

      if (releasedInput === 'touch') {
        document.removeEventListener('touchmove', this.handleTouchMove);
        document.removeEventListener('touchend', this.handleTouchEnd);
        document.removeEventListener('touchcancel', this.handleTouchEnd);
      }

      this.pointerId = null;
      this.dragInput = null;
      this.dragging = false;
      this.classList.remove('is-dragging');
      this.startAutoplay();
    }

    setDragTarget(target) {
      if (!this.scrollEnabled) {
        this.target = target;
        return;
      }

      const boundedTarget = clamp(target, 0, this.scrollItems);
      this.target = boundedTarget;

      // Do not move the document vertically for every horizontal touchmove.
      // The pinned gallery can render the drag independently, then reconcile
      // its scroll position once the gesture has settled.
      if (!this.dragging) this.syncScrollPositionToTarget(boundedTarget);
    }

    syncScrollPositionToTarget(target) {
      const distance = this.getScrollDistance();
      const progress = clamp(target / Math.max(this.scrollItems, 1), 0, 1);
      const nextTop = this.getSectionTop() + distance * progress;

      if (Math.abs(window.scrollY - nextTop) > 1) {
        window.scrollTo({
          top: nextTop,
          behavior: 'auto'
        });
      }
    }

    snapAfterDrag() {
      const startRounded = Math.round(this.startTarget);
      const travel = -(this.lastDeltaX / Math.max(this.cardStep, 1)) * this.dragSensitivity;

      if (mobileQuery.matches) {
        // Ignore touch velocity: a short fast flick must not create an abrupt
        // card change. Only a deliberate swipe of at least 24% of one card is
        // accepted, and accepted gestures use the button navigation method.
        if (Math.abs(travel) >= 0.24) {
          this.target = this.startTarget;
          this.goBy(Math.sign(travel));
        } else {
          this.target = this.startTarget;
        }
        return;
      }

      const releaseAge = Math.max(0, performance.now() - this.lastDragTime);
      const velocityDecay = clamp(1 - releaseAge / 140, 0, 1);
      const releaseVelocity = this.dragVelocityX * velocityDecay;
      const velocityInCards = (-releaseVelocity / Math.max(this.cardStep, 1)) * this.dragSensitivity;
      const projectedTarget = this.target + velocityInCards * 140;
      const distanceThreshold = 0.18;
      const velocityThreshold = 0.4;
      let snapped = startRounded;

      if (Math.abs(travel) >= distanceThreshold || Math.abs(releaseVelocity) >= velocityThreshold) {
        const direction = Math.abs(releaseVelocity) >= velocityThreshold
          ? Math.sign(velocityInCards)
          : Math.sign(travel);

        snapped = Math.round(projectedTarget);
        if (snapped === startRounded && direction !== 0) snapped = startRounded + direction;
      }

      if (this.scrollEnabled) {
        const boundedTarget = clamp(snapped, 0, this.scrollItems);
        this.target = boundedTarget;
        this.syncScrollPositionToTarget(boundedTarget);
      } else {
        this.setDragTarget(snapped);
      }
    }

    onPointerDown(event) {
      if (!this.canStartDrag(event.target)) return;
      if (this.pointerId !== null) return;
      if (event.pointerType === 'touch' && 'ontouchstart' in window) return;
      if (event.pointerType === 'mouse' && event.button > 0) return;

      const didBegin = this.beginDrag(event.pointerId, event.clientX, event.clientY, 'pointer');
      if (!didBegin) return;
      try {
        this.gallery.setPointerCapture?.(event.pointerId);
      } catch (error) {
        // Pointer capture is an enhancement; dragging still works without it.
      }
    }

    onPointerMove(event) {
      if (this.dragInput !== 'pointer' || this.pointerId !== event.pointerId) return;
      this.updateDrag(event.clientX, event.clientY, event);
    }

    onPointerUp(event) {
      if (this.dragInput !== 'pointer' || this.pointerId !== event.pointerId) return;
      this.endDrag(event.type !== 'pointercancel');
    }

    onTouchStart(event) {
      if (!this.canStartDrag(event.target) || event.touches.length !== 1 || this.pointerId !== null) return;

      const touch = event.touches[0];
      const didBegin = this.beginDrag(touch.identifier, touch.clientX, touch.clientY, 'touch');
      if (!didBegin) return;

      document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      document.addEventListener('touchend', this.handleTouchEnd);
      document.addEventListener('touchcancel', this.handleTouchEnd);
    }

    onTouchMove(event) {
      if (this.dragInput !== 'touch') return;

      const touch = Array.from(event.touches).find((item) => item.identifier === this.pointerId);
      if (!touch) return;

      this.updateDrag(touch.clientX, touch.clientY, event);
    }

    onTouchEnd(event) {
      if (this.dragInput !== 'touch') return;

      const activeTouchStillDown = Array.from(event.touches).some((item) => item.identifier === this.pointerId);
      if (activeTouchStillDown) return;

      this.endDrag(event.type !== 'touchcancel');
    }

    onClickCapture(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }

    goBy(direction) {
      if (this.cards.length <= 1) return;

      const nextTarget = Math.round(this.target) + direction;
      this.restartAutoplay();

      if (this.scrollEnabled) {
        this.scrollToTarget(nextTarget);
      } else {
        this.target = nextTarget;
        if (this.snapEnabled) this.snapTarget();
      }
    }

    scrollToTarget(target) {
      const distance = this.getScrollDistance();
      const boundedTarget = clamp(target, 0, this.scrollItems);
      const progress = boundedTarget / Math.max(this.scrollItems, 1);
      window.scrollTo({
        top: this.getSectionTop() + distance * progress,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
      this.target = boundedTarget;
    }

    scheduleTargetSnap() {
      clearTimeout(this.snapTimer);
      this.snapTimer = window.setTimeout(() => this.snapTarget(), 140);
    }

    scheduleScrollSnap() {
      clearTimeout(this.snapTimer);
      this.snapTimer = window.setTimeout(() => {
        const top = this.getSectionTop();
        const distance = this.getScrollDistance();
        const y = window.scrollY;

        if (y < top || y > top + distance) return;

        const rounded = clamp(Math.round(this.target), 0, this.scrollItems);
        const progress = rounded / Math.max(this.scrollItems, 1);
        window.scrollTo({
          top: top + distance * progress,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }, 180);
    }

    snapTarget() {
      this.target = Math.round(this.target);
    }

    startAutoplay() {
      clearInterval(this.autoplayTimer);
      if (!this.autoplayEnabled || this.scrollEnabled || reducedMotion.matches || this.cards.length <= 1) return;

      this.autoplayTimer = window.setInterval(() => {
        if (document.hidden || this.dragging) return;
        this.goBy(1);
      }, this.autoplayDelay);
    }

    restartAutoplay() {
      if (!this.autoplayEnabled) return;
      clearInterval(this.autoplayTimer);
      this.startAutoplay();
    }

    getSectionTop() {
      return window.scrollY + this.getBoundingClientRect().top;
    }

    getScrollDistance() {
      const galleryHeight = this.gallery.offsetHeight || window.innerHeight;
      return Math.max(1, this.offsetHeight - galleryHeight);
    }

    getAnchorOffset() {
      const offset = Number(this.dataset.anchorOffset);
      return Number.isFinite(offset) ? offset : 88;
    }
  }

  [ELEMENT_NAME, 'cuero-card-loop-gallery-v15', 'cuero-card-loop-gallery-v14', 'cuero-card-loop-gallery-v13', 'cuero-card-loop-gallery-v12', 'cuero-card-loop-gallery-v11'].forEach((name) => {
    if (!customElements.get(name)) {
      customElements.define(name, class extends CueroCardLoopGallery {});
    }
  });
})();
